import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { getGenre, updateGenre, deleteGenre } from '../../../../api';
import Input from '../../../../common/Input';
import ImageLoader from '../../../../common/ImageLoader';
import useRouteStore from '../../../../hooks/useRouteStore';
import { swalConfig, toastConfig } from '../../../../config';

const Spinner = () => (
  <svg className='animate-spin h-3.5 w-3.5' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
    <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
    <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 22 6.477 22 12h-4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z' />
  </svg>
);

const AdminGenre = () => {
  const { id } = useParams();
  const mySwal = withReactContent(Swal);
  const [genre, setGenre] = useState(null);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const setRoute = useRouteStore((state) => state.setRoute);

  useEffect(() => {
    setRoute([
      { name: 'Inicio', url: '/administracion' },
      { name: 'Géneros', url: '/administracion/generos' },
      { name: genre?.name || 'Género', url: `/administracion/generos/${id}` },
    ]);
  }, [genre]);

  useEffect(() => {
    getGenre(id).then((res) => {
      setGenre(res.data);
      setName(res.data.name);
      setLoading(false);
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateGenre(id, { name });
      setGenre((prev) => ({ ...prev, name }));
      toast.success('Género actualizado', toastConfig);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al guardar', toastConfig);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const res = await mySwal.fire({ ...swalConfig.delete, text: `¿Borrar el género "${genre.name}"?` });
    if (res.isConfirmed) {
      setDeleting(true);
      try {
        await deleteGenre(id);
        toast.success('Género eliminado', toastConfig);
        navigate('/administracion/generos');
      } catch (err) {
        toast.error(err.response?.data?.message || 'Error al eliminar', toastConfig);
        setDeleting(false);
      }
    }
  };

  return (
    <div className='font-montserrat w-full'>
      {!loading ? (
        <>
          <div className='flex items-center justify-between gap-3 pb-1 mb-5 border-b-2'>
            <h1 className='font-semibold text-xl min-w-0 truncate'>Género: {genre.name}</h1>
            <div className='flex gap-2 shrink-0'>
              <button
                onClick={handleSave}
                disabled={saving || deleting}
                className='flex items-center gap-1.5 bg-red-1 hover:bg-red-2 text-white text-sm font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
              >
                {saving && <Spinner />}
                Guardar
              </button>
              <button
                onClick={handleDelete}
                disabled={saving || deleting}
                className='flex items-center gap-1 text-sm font-semibold text-gray-400 border border-gray-200 px-3 py-1.5 rounded-lg hover:border-red-1 hover:text-red-1 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
              >
                {deleting && <Spinner />}
                <svg className='w-3.5 h-3.5' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
                  <path strokeLinecap='round' strokeLinejoin='round' d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' />
                </svg>
                Borrar
              </button>
            </div>
          </div>

          <div className='max-w-md mb-6'>
            <Input id='name' value={name} onChange={(e) => setName(e.target.value)} label='Nombre' labelClassName='w-24' />
          </div>

          <hr className='my-5' />

          <div>
            <h2 className='font-semibold text-lg mb-3'>
              Series asignadas{' '}
              <span className='text-slate-400 font-normal text-base'>({genre.series?.length ?? 0})</span>
            </h2>
            {genre.series?.length > 0 ? (
              <div className='flex flex-col gap-2'>
                {genre.series.map((serie) => (
                  <Link
                    key={serie._id}
                    to={`/administracion/series/${serie._id}`}
                    className='flex items-center gap-3 bg-white border border-gray-100 rounded-lg px-4 py-3 shadow-sm hover:border-red-1 transition-colors'
                  >
                    <div className='w-9 aspect-[4/6] shrink-0 overflow-hidden rounded bg-slate-100'>
                      <ImageLoader src={serie.imagen_catalogo} />
                    </div>
                    <div>
                      <div className='font-semibold text-sm text-gray-800'>{serie.titulo_original}</div>
                      <div className='text-xs text-slate-400'>{serie.titulo}</div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className='text-sm text-gray-400 italic text-center py-12'>Ninguna serie asignada a este género.</p>
            )}
          </div>
        </>
      ) : (
        <div className='flex flex-col gap-2'>
          {[...Array(4)].map((_, i) => (
            <div key={i} className='h-12 w-full rounded-lg animate-pulse bg-gray-100' />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminGenre;
